/***
 *
 *   TICKET DETAILS
 *   Two-pane drawer. In the main column only the conversation scrolls: the
 *   header, the description/attachments band and the composer are all fixed
 *   around it, so the things you keep referring to while replying stay on
 *   screen. The right rail keeps properties, requester and actions in view and
 *   scrolls on its own.
 *
 *   Replaces the Details / Activity tab split: comments and activity are the
 *   reason this drawer gets opened, so they are no longer one click away, and
 *   nothing they need for context disappears when you go to read them. Below
 *   `lg` the rail folds into the scroll, above the conversation — it is
 *   reference material there, not something you read against.
 *
 **********/

import React, { useCallback, useEffect, useRef } from 'react';
import Style from './details.tailwind';
import TicketHistory, { isComment } from './history';
import TicketDetailsNav from './nav';
import TicketContextBand from './context';
import TicketGeneralInfo from './general';
import TicketOwner from './owner';
import TicketCommands from './commands';
import { Chat } from '../history/chat/chat';
import { useLiveQuery } from 'components/hooks/websocket';
import { useSearchParams } from 'react-router-dom';
import { findTicketStatus } from 'utilities/tickets';
import { useProjectContext } from 'context/ProjectContext';

export const TicketDetails = () => {
    let [searchParams, setSearchParams] = useSearchParams();
    const { switchProject, selectedProject, projects } = useProjectContext();
    const scrollRef = useRef(null);

    const { data, status } = useLiveQuery('tickets', {
        number: searchParams.get('id'),
        ticket_category_id: searchParams.get('c'),
        user_id: null, // Make sure that we are not filtering by user_id
    });

    // HACK: On sync, a ticket with another number is returned
    const ticket = data
        ? data[0]?.number === searchParams.get('id')
            ? data[0]
            : null
        : null;

    useEffect(() => {
        // update params and switch project if ticket is not in current project
        if (ticket && ticket.ticket_category.project_id !== selectedProject?.id) {
            searchParams.set(
                'p',
                projects.find((item) => item.id === ticket.ticket_category.project_id).name,
            );
            setSearchParams(searchParams);
            switchProject(ticket.ticket_category.project_id);
        }
    }, [selectedProject, ticket]);

    const scrollToLatest = useCallback(() => {
        // Wait for the new entry to render before jumping the feed to it.
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        });
    }, []);

    if (!ticket) {
        return (
            <div className={Style.shell}>
                <TicketDetailsNav ticket={null} />
                <div className={Style.loading}>
                    {(status === 'loading' || !data) && (
                        <div className={Style.spinner} aria-hidden />
                    )}
                </div>
            </div>
        );
    }

    // Separate media with image_id and those without
    const medias = ticket.attachments.reduce(
        (acc, media) => {
            if (media.image_id) {
                acc.withImage.push(media);
            } else {
                acc.withFile.push(media);
            }
            return acc;
        },
        { withImage: [], withFile: [] },
    );

    const ticketStatus = findTicketStatus(ticket);
    const requester = ticket.histories?.find((history) => history.action_type === 'created');
    const commentCount = ticket.histories?.filter(isComment).length || 0;

    const rail = (
        <>
            <TicketGeneralInfo ticket={ticket} status={ticketStatus} />
            <TicketOwner ticket={ticket} />
            <TicketCommands ticket={ticket} />
        </>
    );

    return (
        <div className={Style.shell}>
            <TicketDetailsNav
                ticket={ticket}
                status={ticketStatus}
                requester={requester}
                commentCount={commentCount}
            />

            <div className={Style.split}>
                <div className={Style.mainCol}>
                    <TicketContextBand ticket={ticket} medias={medias} />

                    {/* The only scroll in this column. */}
                    <div ref={scrollRef} className={Style.scroll}>
                        <div className={Style.railInline}>{rail}</div>
                        <TicketHistory ticket={ticket} />
                    </div>

                    <div className={Style.composer}>
                        <Chat ticket={ticket} onPosted={scrollToLatest} />
                    </div>
                </div>

                <aside className={Style.rail}>{rail}</aside>
            </div>
        </div>
    );
};
